import { forwardRef } from 'react';
import clsx from 'clsx';
import type {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
  TrackingProp
} from '../types';
import styles from './box.module.less';

const PADS = ['none', 'xs', 'sm', 'md', 'lg', 'xl'] as const;

// eslint-disable-next-line
type Pad = (typeof PADS)[number] | number | (string & {}); // (string & {}) for better IntelliSense - see https://stackoverflow.com/a/61048124
type BoxBaseProps = {
  tracking?: TrackingProp;
  padding?: Pad | { xs?: Pad; sm?: Pad; md?: Pad; lg?: Pad; xl?: Pad };
  variant?: 'blue' | 'darkblue' | 'form-section' | 'yellow' | 'white';
};

export type BoxProps<As extends React.ElementType = 'div'> =
  PolymorphicComponentPropWithRef<As, BoxBaseProps>;

type BoxComponent = <As extends React.ElementType = 'div'>(
  props: BoxProps<As>
) => JSX.Element;

export const Box: BoxComponent = forwardRef(function Box<
  As extends React.ElementType = 'div'
>(
  { as, className, padding, style, variant = 'blue', ...rest }: BoxProps<As>,
  ref?: PolymorphicRef<As>
) {
  const Tag = as || 'div';
  const css = { ...style };
  const paddings = typeof padding === 'object' ? padding : { xs: padding };

  Object.entries(paddings).forEach(([breakpoint, padding]) => {
    css[`--${breakpoint}-pad`] = Number.isInteger(padding)
      ? `${padding}px`
      : PADS.includes(padding as (typeof PADS)[number])
        ? `var(--pad-size-${padding})`
        : padding;
  });

  return (
    <Tag
      className={clsx(className, styles.box, styles[variant])}
      ref={ref}
      style={css}
      {...rest}
    />
  );
}) as BoxComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element
